import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Edit, Trash2, Loader2, Package, Plus, AlertTriangle, 
  Building2, BarChart3, History, AlertCircle
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRawMaterialsTab({ 
  rawMaterials, 
  kitchenRawInventory,
  isLoading, 
  onRefresh,
  onRefreshKitchenInventory
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRawMaterial, setEditingRawMaterial] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", unit: "kg", category: "General", min_stock_alert: 10
  });

  // Add to kitchen state
  const [isAddToKitchenDialogOpen, setIsAddToKitchenDialogOpen] = useState(false);
  const [selectedKitchenForInventory, setSelectedKitchenForInventory] = useState("");
  const [selectedRawMaterialForKitchen, setSelectedRawMaterialForKitchen] = useState("");

  // Report state
  const [inventoryReport, setInventoryReport] = useState({ transactions: [], summary: {} });
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

  const openAddDialog = () => {
    setEditingRawMaterial(null);
    setFormData({ name: "", unit: "kg", category: "General", min_stock_alert: 10 });
    setIsDialogOpen(true);
  };

  const openEditDialog = (rm) => {
    setEditingRawMaterial(rm.raw_material_id);
    setFormData({
      name: rm.name,
      unit: rm.unit,
      category: rm.category || "General",
      min_stock_alert: rm.min_stock_alert || 10
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.unit) {
      toast.error("Name and unit are required");
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingRawMaterial) {
        await axios.put(`${API}/ops-extended/raw-materials/${editingRawMaterial}`, formData);
        toast.success("Raw material updated");
      } else {
        await axios.post(`${API}/ops-extended/raw-materials`, formData);
        toast.success("Raw material created");
      }
      setIsDialogOpen(false);
      setEditingRawMaterial(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save raw material");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (rawMaterialId) => {
    if (!window.confirm("Delete this raw material?")) return;
    try {
      await axios.delete(`${API}/ops-extended/raw-materials/${rawMaterialId}`);
      toast.success("Raw material deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete raw material");
    }
  };

  const handleAddToKitchenInventory = async () => {
    if (!selectedKitchenForInventory || !selectedRawMaterialForKitchen) {
      toast.error("Please select both kitchen and raw material");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/ops-extended/kitchen-raw-inventory`, {
        kitchen_id: selectedKitchenForInventory,
        raw_material_id: selectedRawMaterialForKitchen,
        quantity: 0
      });
      toast.success("Raw material added to kitchen");
      setIsAddToKitchenDialogOpen(false);
      setSelectedKitchenForInventory("");
      setSelectedRawMaterialForKitchen("");
      onRefresh();
      onRefreshKitchenInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to kitchen");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveFromKitchenInventory = async (inventoryId) => {
    if (!window.confirm("Remove this item from kitchen inventory?")) return;
    try {
      await axios.delete(`${API}/ops-extended/kitchen-raw-inventory/${inventoryId}`);
      toast.success("Item removed from kitchen");
      onRefresh();
      onRefreshKitchenInventory();
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const fetchInventoryReport = async () => {
    try {
      const res = await axios.get(`${API}/ops-extended/inventory-report?limit=200`);
      setInventoryReport(res.data);
      setIsReportDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch inventory report");
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Materials</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1">{rawMaterials.length}</p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Low Stock Items</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1 text-yellow-600">
                  {rawMaterials.filter(rm => rm.is_low_stock).length}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Kitchens</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1">{kitchenRawInventory.length}</p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Categories</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1">
                  {[...new Set(rawMaterials.map(rm => rm.category))].length}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={openAddDialog} className="rounded-full" data-testid="add-raw-material-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Raw Material
        </Button>
        <Button variant="outline" onClick={() => setIsAddToKitchenDialogOpen(true)} className="rounded-full">
          <Building2 className="w-4 h-4 mr-2" />
          Add to Kitchen
        </Button>
        <Button variant="outline" onClick={fetchInventoryReport} className="rounded-full">
          <History className="w-4 h-4 mr-2" />
          Transaction Report
        </Button>
      </div>

      {/* Raw Materials Table */}
      <Card className="border-0 shadow-card mb-6" data-testid="raw-materials-table-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg lg:text-xl">Raw Materials Catalog</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0 sm:p-6">
          {isLoading ? (
            <div className="flex justify-center py-8"><div className="spinner"></div></div>
          ) : rawMaterials.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No raw materials yet</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[600px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Total Stock</TableHead>
                    <TableHead>Min Alert</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rawMaterials.map((rm) => (
                    <TableRow key={rm.raw_material_id}>
                      <TableCell className="font-semibold">{rm.name}</TableCell>
                      <TableCell><Badge variant="outline">{rm.unit}</Badge></TableCell>
                      <TableCell>{rm.category}</TableCell>
                      <TableCell className={`font-bold ${rm.is_low_stock ? 'text-yellow-600' : 'text-green-600'}`}>
                        {rm.total_quantity} {rm.unit}
                      </TableCell>
                      <TableCell>{rm.min_stock_alert}</TableCell>
                      <TableCell>
                        {rm.is_low_stock ? (
                          <Badge className="bg-yellow-100 text-yellow-700">Low Stock</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">OK</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(rm)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(rm.raw_material_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kitchen Raw Inventory Overview */}
      <Card className="border-0 shadow-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg lg:text-xl flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Kitchen Raw Materials Inventory
          </CardTitle>
        </CardHeader>
        <CardContent>
          {kitchenRawInventory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No kitchens configured</p>
            </div>
          ) : (
            <div className="space-y-6">
              {kitchenRawInventory.map((kitchen) => (
                <div key={kitchen.kitchen_id} className="border rounded-xl p-4 bg-muted/30">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" />
                      {kitchen.kitchen_name}
                    </h4>
                    <Badge variant="outline" className="text-xs">
                      {kitchen.inventory.length} items
                    </Badge>
                  </div>
                  {kitchen.inventory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No raw materials in this kitchen</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {kitchen.inventory.map((item) => (
                        <div 
                          key={item.inventory_id} 
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            item.quantity <= 0 ? 'bg-red-50 border-red-200' : 
                            item.is_low_stock ? 'bg-yellow-50 border-yellow-200' : 
                            'bg-white'
                          }`}
                        >
                          <div>
                            <p className="font-medium text-sm">{item.raw_material_name}</p>
                            <p className="text-xs text-muted-foreground">{item.unit}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-lg font-bold ${
                              item.quantity <= 0 ? 'text-red-600' : 
                              item.is_low_stock ? 'text-yellow-600' : 
                              'text-green-600'
                            }`}>
                              {item.quantity}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-6 w-6 text-red-500"
                              onClick={() => handleRemoveFromKitchenInventory(item.inventory_id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw Material Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          setEditingRawMaterial(null);
          setFormData({ name: "", unit: "kg", category: "General", min_stock_alert: 10 });
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingRawMaterial ? "Edit Raw Material" : "Add Raw Material"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Sugar, Rice, Oil"
                data-testid="rm-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit *</Label>
                <Select 
                  value={formData.unit} 
                  onValueChange={(v) => setFormData({ ...formData, unit: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kilogram (kg)</SelectItem>
                    <SelectItem value="g">Gram (g)</SelectItem>
                    <SelectItem value="liters">Liters</SelectItem>
                    <SelectItem value="ml">Milliliters (ml)</SelectItem>
                    <SelectItem value="pcs">Pieces</SelectItem>
                    <SelectItem value="packets">Packets</SelectItem>
                    <SelectItem value="boxes">Boxes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Dry Goods">Dry Goods</SelectItem>
                    <SelectItem value="Spices">Spices</SelectItem>
                    <SelectItem value="Dairy">Dairy</SelectItem>
                    <SelectItem value="Vegetables">Vegetables</SelectItem>
                    <SelectItem value="Oils">Oils</SelectItem>
                    <SelectItem value="Packaging">Packaging</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Minimum Stock Alert</Label>
              <Input
                type="number"
                value={formData.min_stock_alert}
                onChange={(e) => setFormData({ ...formData, min_stock_alert: parseFloat(e.target.value) || 0 })}
                placeholder="10"
              />
              <p className="text-xs text-muted-foreground">Alert when total stock falls below this value</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="rounded-full">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingRawMaterial ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Kitchen Dialog */}
      <Dialog open={isAddToKitchenDialogOpen} onOpenChange={setIsAddToKitchenDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Raw Material to Kitchen</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Select Kitchen *</Label>
              <Select value={selectedKitchenForInventory} onValueChange={setSelectedKitchenForInventory}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a kitchen" />
                </SelectTrigger>
                <SelectContent>
                  {kitchenRawInventory.map((k) => (
                    <SelectItem key={k.kitchen_id} value={k.kitchen_id}>{k.kitchen_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Raw Material *</Label>
              <Select value={selectedRawMaterialForKitchen} onValueChange={setSelectedRawMaterialForKitchen}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a raw material" />
                </SelectTrigger>
                <SelectContent>
                  {rawMaterials.map((rm) => (
                    <SelectItem key={rm.raw_material_id} value={rm.raw_material_id}>
                      {rm.name} ({rm.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Initial quantity will be set to 0. Ops managers can update the quantity later.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsAddToKitchenDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleAddToKitchenInventory} disabled={isSubmitting} className="rounded-full">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add to Kitchen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Report Dialog */}
      <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <History className="w-5 h-5" />
              Inventory Transaction Report
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-muted/30">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Transactions</p>
                  <p className="text-2xl font-bold">{inventoryReport.summary?.total_transactions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-green-600">Total Additions</p>
                  <p className="text-2xl font-bold text-green-600">+{inventoryReport.summary?.total_additions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-red-600">Total Subtractions</p>
                  <p className="text-2xl font-bold text-red-600">-{inventoryReport.summary?.total_subtractions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-blue-600">Net Change</p>
                  <p className={`text-2xl font-bold ${(inventoryReport.summary?.net_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(inventoryReport.summary?.net_change || 0) >= 0 ? '+' : ''}{inventoryReport.summary?.net_change || 0}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Transactions Table */}
            {inventoryReport.transactions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No transactions recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Kitchen</TableHead>
                      <TableHead>Raw Material</TableHead>
                      <TableHead>Adjustment</TableHead>
                      <TableHead>Before → After</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventoryReport.transactions?.map((txn) => (
                      <TableRow key={txn.transaction_id}>
                        <TableCell className="text-sm">
                          {new Date(txn.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>{txn.kitchen_name}</TableCell>
                        <TableCell>{txn.raw_material_name}</TableCell>
                        <TableCell>
                          <Badge className={txn.adjustment >= 0 ? 'bg-green-500' : 'bg-red-500'}>
                            {txn.adjustment >= 0 ? '+' : ''}{txn.adjustment}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {txn.quantity_before} → {txn.quantity_after}
                        </TableCell>
                        <TableCell className="text-sm">{txn.reason}</TableCell>
                        <TableCell className="text-sm">{txn.user_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReportDialogOpen(false)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
